import {
    getConfig,
    AppConfig,
    PaymentLogo,
} from '@superbet-group/web.lib.config';
import {
    CitiesMap,
    Country as CountryCode,
    County,
} from '@superbet-group/web.types';
import { loadScript } from '@superbet-group/web.lib.utilities';
import {
    Dictionary,
    keyBy as _keyBy,
} from 'lodash';
import {
    ActionContext,
    Commit,
    Dispatch,
} from 'vuex';

import StoreUtil from '@app/store/StoreUtil';
import Country from '@core/models/country/Country';
import Shop from '@core/models/country/Shop';
import countryService from '@core/services/country/CountryService';
import ScheduledUtil from '@core/utils/scheduled/ScheduledUtil';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { IState } from '@store/index';
import { sortShops } from '@store/modules/data/country/utils';
const ms = require('ms');

export const SET_CONFIG = 'SET_CONFIG';
export const SET_SHOPS = 'SET_SHOPS';
export const SET_COUNTRIES = 'SET_COUNTRIES';
export const SET_PAYMENT_PROVIDERS_LOGOS = 'SET_PAYMENT_PROVIDERS_LOGOS';
export const SET_PREFERRED_SHOPS = 'SET_PREFERRED_SHOPS';
export const SET_COUNTIES = 'SET_COUNTIES';
export const SET_CITIES_MAP = 'SET_CITIES_MAP';

export interface ICountryState {
    config: AppConfig;
    shops: Shop[];
    paymentProvidersLogos: PaymentLogo[];
    countries: Country[];
    preferredShops: string[];
    counties: County[];
    citiesMap: CitiesMap;
}

interface ICountryGetters {
    config: AppConfig;
    shops: Shop[];
    countries: Country[];
    whitelistedCountries: Country[];
    preselectedCountry: Country;
    whitelistedPreselectedCountry: Country;
    preferredShops: string[];
    shopsMap: Dictionary<Shop>;
    counties: County[];
    citiesMap: CitiesMap;
}

type CountiesCountryCode = Extract<CountryCode, CountryCode.Ro | CountryCode.Pl>;

function defaultState(): ICountryState {
    return {
        config: getConfig(),
        shops: [],
        countries: [],
        paymentProvidersLogos: [],
        preferredShops: [],
        counties: [],
        citiesMap: {},
    };
}

export default {
    namespaced: true,

    state: defaultState(),

    mutations: {
        [SET_CONFIG]: StoreUtil.createSimpleMutator('config'),
        [SET_SHOPS]: StoreUtil.createSimpleMutator('shops'),
        [SET_COUNTRIES]: StoreUtil.createSimpleMutator('countries'),
        [SET_PAYMENT_PROVIDERS_LOGOS]: StoreUtil.createSimpleMutator('paymentProvidersLogos'),
        [SET_PREFERRED_SHOPS]: StoreUtil.createSimpleMutator('preferredShops'),
        [SET_COUNTIES]: StoreUtil.createSimpleMutator('counties'),
        [SET_CITIES_MAP]: StoreUtil.createSimpleMutator('citiesMap'),
    },

    getters: {
        config: StoreUtil.createSimpleGetter('config'),
        shops: StoreUtil.createSimpleGetter('shops'),
        countries: StoreUtil.createSimpleGetter('countries'),
        paymentProvidersLogos: StoreUtil.createSimpleGetter('paymentProvidersLogos'),
        whitelistedCountries: (state: ICountryState, getters: ICountryGetters) => {
            if (shellLocalConfig.countryCodesWhitelist.length > 0) {
                return getters.countries.filter((country: Country) => shellLocalConfig.countryCodesWhitelist?.includes(country.code));
            } else {
                return state.countries;
            }
        },
        preselectedCountry: (state: ICountryState) => {
            let preselectedCountry;
            if (state.countries) {
                preselectedCountry = state.countries.find((country) => country.selected);
                if (!preselectedCountry) {
                    const defaultCountryPrefix = getConfig()
                        .countrySpecific.user.settings.defaultCountryPrefix;
                    preselectedCountry = state.countries
                        .find((country) => country.prefix === defaultCountryPrefix) ?? state.countries[0];
                }
            }

            return preselectedCountry;
        },
        preselectedWhitelistedCountry: (state: ICountryState, getters: ICountryGetters) => {
            if (getters.whitelistedCountries) {
                if (getters.preselectedCountry && getters.whitelistedCountries.includes(getters.preselectedCountry)) {
                    return getters.preselectedCountry;
                } else {
                    return getters.whitelistedCountries[0];
                }
            }
        },
        shopsMap: (state: ICountryState) => _keyBy(state.shops, (shop) => `${shop.city} ${shop.address}`),
        preferredShops: (state: ICountryState, getters: any) =>
            state.preferredShops.map((shop) => getters.shopsMap[shop]),
        counties: StoreUtil.createSimpleGetter('counties'),
        citiesMap: StoreUtil.createSimpleGetter('citiesMap'),
    },

    actions: {
        startScheduledBannersCheck: ({ dispatch }: {dispatch: Dispatch}) => {
            dispatch('fetchBanners');
            ScheduledUtil.scheduleTask(
                () => dispatch('fetchBanners'),
                ms('30m'),
            );
        },
        initializeConfig: (context: ActionContext<ICountryState, IState>, config: AppConfig) => {
            context.commit(SET_CONFIG, config);
        },
        fetchShops: StoreUtil.asyncErrorGuard(async ({ commit }: {commit: Commit}) => {
            const shops = await countryService.getShops();

            commit(SET_SHOPS, sortShops(shops));
        }),
        fetchCountries: StoreUtil.asyncErrorGuard(async ({ commit }: {commit: Commit}) => {
            const countries = await countryService.getCountries();

            commit(SET_COUNTRIES, countries);
        }),
        loadStaticCountries: StoreUtil.asyncErrorGuard(async ({ commit }: {commit: Commit}) => {
            const countriesUrl = `${window.location.origin}/static/js/countries.js`;
            await loadScript(countriesUrl, 'countries');

            const countries = window.data?.countries || [];

            commit(SET_COUNTRIES, countries);
        }),
        fetchPaymentProvidersLogos: StoreUtil.asyncErrorGuard(async ({ commit }: {commit: Commit}) => {
            const paymentProvidersLogos = await countryService.getPaymentProvidersLogos();

            commit(SET_PAYMENT_PROVIDERS_LOGOS, paymentProvidersLogos);
        }),
        loadCounties: StoreUtil.asyncErrorGuard(async ({ commit }: {commit: Commit}, countryCode: CountiesCountryCode) => {
            await loadScript(`${window.location.origin}/static/js/counties-${countryCode}.js`, `${countryCode}Counties`);

            const counties = window.data?.counties || [];

            const cities = counties.reduce((citiesMap: CitiesMap, county: County) => {
                // eslint-disable-next-line no-param-reassign
                citiesMap[county.id] = county.cities;
                return citiesMap;
            }, {});

            commit(SET_COUNTIES, counties);
            commit(SET_CITIES_MAP, cities);
        }),
    },
};
